/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType, RuleMatchType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ActiveTimelineProps {
  id: string;
  timelineId: string;
  companyId: string;
  matchType: RuleMatchType;
  entityType: EntityType;
  entityId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ActiveTimelineRecord extends Record<ActiveTimelineProps>({
  id: null as any,
  timelineId: null as any,
  companyId: null as any,
  matchType: null as any,
  entityType: null as any,
  entityId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    timelineId: string;
    companyId: string;
    matchType: RuleMatchType;
    entityType: EntityType;
    entityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ActiveTimelineRecord {
    if (json == null) {
      throw new Error('ActiveTimelineRecord.fromJS: parameter cannot be null');
    }
    return new ActiveTimelineRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ActiveTimelineJson.id',
      ),
      timelineId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'timelineId', '00000000-0000-0000-0000-000000000000'),
        'ActiveTimelineJson.timelineId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ActiveTimelineJson.companyId',
      ),
      matchType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
        'ActiveTimelineJson.matchType',
      ),
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'ActiveTimelineJson.entityType',
      ),
      entityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
        'ActiveTimelineJson.entityId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    timelineId: string;
    companyId: string;
    matchType: RuleMatchType;
    entityType: EntityType;
    entityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ActiveTimelineRecord, Error[]] {
    if (json == null) {
      throw new Error('ActiveTimelineRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ActiveTimelineRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ActiveTimelineJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timelineId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'timelineId', '00000000-0000-0000-0000-000000000000'),
            'ActiveTimelineJson.timelineId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ActiveTimelineJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      matchType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
            'ActiveTimelineJson.matchType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'ActiveTimelineJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
            'ActiveTimelineJson.entityId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ActiveTimelineProps) {
    super(props);
    if (props == null) {
      throw new Error('ActiveTimelineRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ActiveTimelineViewModelBase implements ActiveTimelineProps {
  protected readonly _props: ActiveTimelineProps;
  private _json?: ActiveTimelineProps;

  get id(): string {
    return this._props.id;
  }
  get timelineId(): string {
    return this._props.timelineId;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get matchType(): RuleMatchType {
    return this._props.matchType;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }
  get entityId(): string {
    return this._props.entityId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isDropoffLocation() {
    return this._props.matchType === RuleMatchType.DropoffLocation;
  }
  public get isPickupLocation() {
    return this._props.matchType === RuleMatchType.PickupLocation;
  }
  public get isMatchTypeUnspecified() {
    return this._props.matchType === RuleMatchType.Unspecified;
  }
  public get isEntityTypeUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyMatchType(...ruleMatchType: RuleMatchType[]) {
    return ruleMatchType.includes(this._props.matchType);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }

  constructor(props: ActiveTimelineProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        timelineId: this._props.timelineId,
        companyId: this._props.companyId,
        matchType: this._props.matchType,
        entityType: this._props.entityType,
        entityId: this._props.entityId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllActiveTimelineEntities = (state: AppState) =>
  state.entityStores.activeTimeline.entities;

export const selectActiveTimelineById = (id: string) => (state: AppState) =>
  state.entityStores.activeTimeline.entities.get(id);

export const selectActiveTimelineMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.activeTimeline.meta.get(id);

export const selectActiveTimelineAndMetadataById = (id: string) =>
  createSelector(
    selectActiveTimelineById(id),
    selectActiveTimelineMetadataById(id),
    (activeTimeline, meta) => ({ activeTimeline, meta }),
  );

export function selectActiveTimelineEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllActiveTimelineEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ActiveTimelineRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectActiveTimelineEntitiesByTimelineId(timelineId: string) {
  return createSelector(selectAllActiveTimelineEntities, (entities) =>
    entities.filter((e) => e.timelineId === timelineId).valueSeq(),
  );
}

export function selectActiveTimelineEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllActiveTimelineEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectActiveTimelineEntitiesByMatchType(matchType: RuleMatchType) {
  return createSelector(selectAllActiveTimelineEntities, (entities) =>
    entities.filter((e) => e.matchType === matchType).valueSeq(),
  );
}

export function selectActiveTimelineEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllActiveTimelineEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectActiveTimelineEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllActiveTimelineEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}
//#endregion Selectors
